import React from 'react';
import { Box, Collapse, Drawer, useColorScheme } from '@mui/material';

import {
  ButtonTertiaryIcon,
  GlassButtonSecondary,
  GlassColors,
} from '../../../components/glassmorphism/GlassmorphismStyles';

import { ReactComponent as CloseIcon } from '../../../assets/icons/nav/close.svg';
import { BrowseHandymanSortFields } from '../../../configuration/api/apiSchema';

type Props = {
  sortOpen: boolean;
  isMobile: boolean;
  isDark: boolean;

  sort: BrowseHandymanSortFields;
  setSort: React.Dispatch<React.SetStateAction<BrowseHandymanSortFields>>;
  setIsDesc: (v: boolean) => void;

  setMobileSortOpen: (v: boolean) => void;
};

export default function BrowseHandymanSort(props: Props) {
  const {
    sortOpen,
    isMobile,
    isDark,
    sort,
    setIsDesc,
    setSort,
    setMobileSortOpen,
  } = props;
  return (
    <>
      {isMobile ? (
        <Drawer
          anchor='bottom'
          open={sortOpen}
          onClose={() => setMobileSortOpen(false)}
          sx={{ zIndex: 999999 }}
          PaperProps={{
            sx: {
              height: '100dvh',
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              padding: '24px',
              backgroundImage: 'none',
              backgroundColor: isDark
                ? 'rgba(26, 26, 26, 1)'
                : 'rgba(229, 229, 220, 1)',
            },
          }}
        >
          {/* Sort Actions Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '24px',
              width: '100%',
              position: 'relative',
              mb: '24px',
            }}
          >
            {/* Title */}
            <Box
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '24px',
                lineHeight: '133%',
                color: isDark ? '#00FFFF' : '#005CAD',
              }}
            >
              Sort
            </Box>
            <ButtonTertiaryIcon
              onClick={() => setMobileSortOpen(false)}
              sx={{
                position: 'absolute',
                zIndex: 1,
                width: '32px',
                height: '32px',
                top: 0,
                right: 0,
                '& svg': {
                  fontSize: '16px',
                  '& path': {
                    fill: '#E2F7FF',
                  },
                },
              }}
            >
              <CloseIcon />
            </ButtonTertiaryIcon>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            <GlassButtonSecondary
              onClick={() => {
                setSort(BrowseHandymanSortFields.Default);
                setIsDesc(true);
              }}
              sx={{
                fontSize: '12px',
                lineHeight: 2,
                padding: '8px 24px',
                border: 0,
                alignSelf: 'start',
                background: isDark
                  ? sort === 'Default'
                    ? GlassColors.accent
                    : 'inherit'
                  : sort === 'Default'
                    ? GlassColors.secondary
                    : 'inherit',
                boxShadow: `
                          0px 0px 16px 0px #5C5C5C4D inset,
                          0px 0px 3px 0px #FFFFFF66 inset,
                          -1px -1px 0.5px -1px #FFFFFF inset,
                          1px 1px 0.5px -1px #FFFFFF66 inset,
                          -1px -1px 0px -0.5px #FFFFFF80 inset,
                          1px 1px 0px -0.5px #FFFFFF99 inset,
                          0px 1px 8px 0px #0000001F,
                          0px 0px 2px 0px #0000001A
                        `,
                '&&': {
                  color: isDark
                    ? sort === 'Default'
                      ? GlassColors.light.textSecondary
                      : '#E5E5DC'
                    : sort === 'Default'
                      ? '#E5E5DC'
                      : GlassColors.light.textSecondary,
                },
                '&:hover': {
                  backgroundColor: isDark
                    ? GlassColors.accent
                    : sort === 'Default'
                      ? GlassColors.secondary
                      : 'inherit',
                  color: isDark
                    ? GlassColors.light.textSecondary
                    : sort === 'Default'
                      ? '#E5E5DC'
                      : GlassColors.light.textSecondary,
                },
              }}
            >
              Recommended
            </GlassButtonSecondary>
            <GlassButtonSecondary
              onClick={() => {
                setSort(BrowseHandymanSortFields.Price);
                setIsDesc(false);
              }}
              sx={{
                fontSize: '12px',
                lineHeight: 2,
                padding: '8px 24px',
                alignSelf: 'start',
                border: 0,
                background: isDark
                  ? sort === 'Price'
                    ? GlassColors.accent
                    : 'inherit'
                  : sort === 'Price'
                    ? GlassColors.secondary
                    : 'inherit',
                boxShadow: `
                          0px 0px 16px 0px #5C5C5C4D inset,
                          0px 0px 3px 0px #FFFFFF66 inset,
                          -1px -1px 0.5px -1px #FFFFFF inset,
                          1px 1px 0.5px -1px #FFFFFF66 inset,
                          -1px -1px 0px -0.5px #FFFFFF80 inset,
                          1px 1px 0px -0.5px #FFFFFF99 inset,
                          0px 1px 8px 0px #0000001F,
                          0px 0px 2px 0px #0000001A
                        `,
                '&&': {
                  color: isDark
                    ? sort === 'Price'
                      ? GlassColors.light.textSecondary
                      : '#E5E5DC'
                    : sort === 'Price'
                      ? '#E5E5DC'
                      : GlassColors.light.textSecondary,
                },
                '&:hover': {
                  backgroundColor: isDark
                    ? GlassColors.accent
                    : sort === 'Price'
                      ? GlassColors.secondary
                      : 'inherit',
                  color: isDark
                    ? GlassColors.light.textSecondary
                    : sort === 'Price'
                      ? '#E5E5DC'
                      : GlassColors.light.textSecondary,
                },
              }}
            >
              Price: Low to High
            </GlassButtonSecondary>
            <GlassButtonSecondary
              onClick={() => {
                setSort(BrowseHandymanSortFields.Rating);
                setIsDesc(true);
              }}
              sx={{
                fontSize: '12px',
                lineHeight: 2,
                padding: '8px 24px',
                border: 0,
                alignSelf: 'start',
                background: isDark
                  ? sort === 'Rating'
                    ? GlassColors.accent
                    : 'inherit'
                  : sort === 'Rating'
                    ? GlassColors.secondary
                    : 'inherit',
                boxShadow: `
                          0px 0px 16px 0px #5C5C5C4D inset,
                          0px 0px 3px 0px #FFFFFF66 inset,
                          -1px -1px 0.5px -1px #FFFFFF inset,
                          1px 1px 0.5px -1px #FFFFFF66 inset,
                          -1px -1px 0px -0.5px #FFFFFF80 inset,
                          1px 1px 0px -0.5px #FFFFFF99 inset,
                          0px 1px 8px 0px #0000001F,
                          0px 0px 2px 0px #0000001A
                        `,
                '&&': {
                  color: isDark
                    ? sort === 'Rating'
                      ? GlassColors.light.textSecondary
                      : '#E5E5DC'
                    : sort === 'Rating'
                      ? '#E5E5DC'
                      : GlassColors.light.textSecondary,
                },
                '&:hover': {
                  backgroundColor: isDark
                    ? GlassColors.accent
                    : sort === 'Rating'
                      ? GlassColors.secondary
                      : 'inherit',
                  color: isDark
                    ? GlassColors.light.textSecondary
                    : sort === 'Rating'
                      ? '#E5E5DC'
                      : GlassColors.light.textSecondary,
                },
              }}
            >
              Rating: Highest First
            </GlassButtonSecondary>
            <GlassButtonSecondary
              onClick={() => {
                setSort(BrowseHandymanSortFields.Review);
                setIsDesc(true);
              }}
              sx={{
                fontSize: '12px',
                lineHeight: 2,
                padding: '8px 24px',
                alignSelf: 'start',
                border: 0,
                background: isDark
                  ? sort === 'Review'
                    ? GlassColors.accent
                    : 'inherit'
                  : sort === 'Review'
                    ? GlassColors.secondary
                    : 'inherit',
                boxShadow: `
                          0px 0px 16px 0px #5C5C5C4D inset,
                          0px 0px 3px 0px #FFFFFF66 inset,
                          -1px -1px 0.5px -1px #FFFFFF inset,
                          1px 1px 0.5px -1px #FFFFFF66 inset,
                          -1px -1px 0px -0.5px #FFFFFF80 inset,
                          1px 1px 0px -0.5px #FFFFFF99 inset,
                          0px 1px 8px 0px #0000001F,
                          0px 0px 2px 0px #0000001A
                        `,
                '&&': {
                  color: isDark
                    ? sort === 'Review'
                      ? GlassColors.light.textSecondary
                      : '#E5E5DC'
                    : sort === 'Review'
                      ? '#E5E5DC'
                      : GlassColors.light.textSecondary,
                },
                '&&:hover': {
                  backgroundColor: isDark
                    ? GlassColors.accent
                    : sort === 'Review'
                      ? GlassColors.secondary
                      : 'inherit',
                  color: isDark
                    ? GlassColors.light.textSecondary
                    : sort === 'Review'
                      ? '#E5E5DC'
                      : GlassColors.light.textSecondary,
                },
              }}
            >
              Number of Reviews: Most First
            </GlassButtonSecondary>
          </Box>
        </Drawer>
      ) : (
          <Box
            className='btn-holder'
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            mb: '16px',
          }}
        >
          <GlassButtonSecondary
            onClick={() => {
              setSort(BrowseHandymanSortFields.Default);
              setIsDesc(true);
            }}
            sx={{
              fontSize: '12px',
              lineHeight: 2,
              padding: '8px 24px',
              border: 0,
              background: isDark
                ? sort === 'Default'
                  ? GlassColors.accent
                  : 'inherit'
                : sort === 'Default'
                  ? GlassColors.secondary
                  : 'inherit',
              boxShadow: `
                          0px 0px 16px 0px #5C5C5C4D inset,
                          0px 0px 3px 0px #FFFFFF66 inset,
                          -1px -1px 0.5px -1px #FFFFFF inset,
                          1px 1px 0.5px -1px #FFFFFF66 inset,
                          -1px -1px 0px -0.5px #FFFFFF80 inset,
                          1px 1px 0px -0.5px #FFFFFF99 inset,
                          0px 1px 8px 0px #0000001F,
                          0px 0px 2px 0px #0000001A
                        `,
              '&&': {
                color: isDark
                  ? sort === 'Default'
                    ? GlassColors.light.textSecondary
                    : '#E5E5DC'
                  : sort === 'Default'
                    ? '#E5E5DC'
                    : GlassColors.light.textSecondary,
              },
              '&:hover': {
                backgroundColor: isDark
                  ? GlassColors.accent
                  : sort === 'Default'
                    ? GlassColors.secondary
                    : 'inherit',
                color: isDark
                  ? GlassColors.light.textSecondary
                  : sort === 'Default'
                    ? '#E5E5DC'
                    : GlassColors.light.textSecondary,
              },
            }}
          >
            Recommended
          </GlassButtonSecondary>
          <GlassButtonSecondary
            onClick={() => {
              setSort(BrowseHandymanSortFields.Price);
              setIsDesc(false);
            }}
            sx={{
              fontSize: '12px',
              lineHeight: 2,
              padding: '8px 24px',
              border: 0,
              background: isDark
                ? sort === 'Price'
                  ? GlassColors.accent
                  : 'inherit'
                : sort === 'Price'
                  ? GlassColors.secondary
                  : 'inherit',
              boxShadow: `
                          0px 0px 16px 0px #5C5C5C4D inset,
                          0px 0px 3px 0px #FFFFFF66 inset,
                          -1px -1px 0.5px -1px #FFFFFF inset,
                          1px 1px 0.5px -1px #FFFFFF66 inset,
                          -1px -1px 0px -0.5px #FFFFFF80 inset,
                          1px 1px 0px -0.5px #FFFFFF99 inset,
                          0px 1px 8px 0px #0000001F,
                          0px 0px 2px 0px #0000001A
                        `,
              '&&': {
                color: isDark
                  ? sort === 'Price'
                    ? GlassColors.light.textSecondary
                    : '#E5E5DC'
                  : sort === 'Price'
                    ? '#E5E5DC'
                    : GlassColors.light.textSecondary,
              },
              '&:hover': {
                backgroundColor: isDark
                  ? GlassColors.accent
                  : sort === 'Price'
                    ? GlassColors.secondary
                    : 'inherit',
                color: isDark
                  ? GlassColors.light.textSecondary
                  : sort === 'Price'
                    ? '#E5E5DC'
                    : GlassColors.light.textSecondary,
              },
            }}
          >
            Price: Low to High
          </GlassButtonSecondary>
          <GlassButtonSecondary
            onClick={() => {
              setSort(BrowseHandymanSortFields.Rating);
              setIsDesc(true);
            }}
            sx={{
              fontSize: '12px',
              lineHeight: 2,
              padding: '8px 24px',
              border: 0,
              background: isDark
                ? sort === 'Rating'
                  ? GlassColors.accent
                  : 'inherit'
                : sort === 'Rating'
                  ? GlassColors.secondary
                  : 'inherit',
              boxShadow: `
                          0px 0px 16px 0px #5C5C5C4D inset,
                          0px 0px 3px 0px #FFFFFF66 inset,
                          -1px -1px 0.5px -1px #FFFFFF inset,
                          1px 1px 0.5px -1px #FFFFFF66 inset,
                          -1px -1px 0px -0.5px #FFFFFF80 inset,
                          1px 1px 0px -0.5px #FFFFFF99 inset,
                          0px 1px 8px 0px #0000001F,
                          0px 0px 2px 0px #0000001A
                        `,
              '&&': {
                color: isDark
                  ? sort === 'Rating'
                    ? GlassColors.light.textSecondary
                    : '#E5E5DC'
                  : sort === 'Rating'
                    ? '#E5E5DC'
                    : GlassColors.light.textSecondary,
              },
              '&:hover': {
                backgroundColor: isDark
                  ? GlassColors.accent
                  : sort === 'Rating'
                    ? GlassColors.secondary
                    : 'inherit',
                color: isDark
                  ? GlassColors.light.textSecondary
                  : sort === 'Rating'
                    ? '#E5E5DC'
                    : GlassColors.light.textSecondary,
              },
            }}
          >
            Rating: Highest First
          </GlassButtonSecondary>
          <GlassButtonSecondary
            onClick={() => {
              setSort(BrowseHandymanSortFields.Review);
              setIsDesc(true);
            }}
            sx={{
              fontSize: '12px',
              lineHeight: 2,
              padding: '8px 24px',
              border: 0,
              background: isDark
                ? sort === 'Review'
                  ? GlassColors.accent
                  : 'inherit'
                : sort === 'Review'
                  ? GlassColors.secondary
                  : 'inherit',
              boxShadow: `
                          0px 0px 16px 0px #5C5C5C4D inset,
                          0px 0px 3px 0px #FFFFFF66 inset,
                          -1px -1px 0.5px -1px #FFFFFF inset,
                          1px 1px 0.5px -1px #FFFFFF66 inset,
                          -1px -1px 0px -0.5px #FFFFFF80 inset,
                          1px 1px 0px -0.5px #FFFFFF99 inset,
                          0px 1px 8px 0px #0000001F,
                          0px 0px 2px 0px #0000001A
                        `,
              '&&': {
                color: isDark
                  ? sort === 'Review'
                    ? GlassColors.light.textSecondary
                    : '#E5E5DC'
                  : sort === 'Review'
                    ? '#E5E5DC'
                    : GlassColors.light.textSecondary,
              },
              '&&:hover': {
                backgroundColor: isDark
                  ? GlassColors.accent
                  : sort === 'Review'
                    ? GlassColors.secondary
                    : 'inherit',
                color: isDark
                  ? GlassColors.light.textSecondary
                  : sort === 'Review'
                    ? '#E5E5DC'
                    : GlassColors.light.textSecondary,
              },
            }}
          >
            Number of Reviews: Most First
          </GlassButtonSecondary>
        </Box>
      )}
    </>
  );
}
